<template>
  <CardComponent :padding="false" class="gap-0">
    <div class="flex justify-between items-center p-6 border-b-2 border-grey-light-200">
      <div>
        <h3 class="font-semibold text-lg text-grey-light-900">Payment Plan Value</h3>
        <p class="text-grey-light-600 text-xs">Placed, Completed, Cancelled: last 12 months</p>
      </div>
      <Button
        size="icon"
        variant="ghost"
        type="button"
        class="border border-grey-light-200 h-12 w-12 rounded-lg flex justify-center items-center shadow-xs"
        @click="showSection === 'graph' ? (showSection = 'table') : (showSection = 'graph')"
      >
        <Table2 v-if="showSection === 'graph'" class="h-6 w-6" />
        <ChartNoAxesColumn v-else class="h-6 w-6" />
      </Button>
    </div>
    <Skeleton v-if="isLoading" class="h-full w-full" />
    <template v-else>
      <div class="p-4" v-if="showSection === 'graph'">
        <BarChart
          v-if="showSection === 'graph' && !isLoading"
          index="name"
          :data="graphData"
          :colors="['#079455', '#38A6B3']"
          :y-formatter="(tick) => (typeof tick === 'number' ? displayCurrency(tick) : '')"
          :format-currency="true"
          :categories="['Total Payment Plan Value', 'Average Payment Plan Value']"
          :rounded-corners="4"
        />
      </div>
      <div class="pb-6" v-else-if="showSection === 'table'">
        <DataTable :columns="paymentPlanValueColumns" :data="graphData" :show-pagination="false" />
      </div>
    </template>
  </CardComponent>
</template>

<script setup lang="ts">
import CardComponent from '@/components/merchant/CardComponent.vue';
import DataTable from '@/components/merchant/DataTable.vue';
import { Button } from '@/components/ui/button';
import { BarChart } from '@/components/ui/chart-bar';
import { Skeleton } from '@/components/ui/skeleton';
import { NetworkError } from '@/errors';
import type { MappedPaymentPlanValueGraphData, PaymentPlanValueGraphData } from '@/models/graphs';
import { getPaymentPlanGraphsData } from '@/services/graph';
import { useMerchantStore } from '@/stores/merchants';
import { displayCurrency } from '@/utils/format.utils';
import { addMetric, createException, createWarning } from '@/utils/monitoring.utils';
import dayjs from 'dayjs';
import { ChartNoAxesColumn, Table2 } from 'lucide-vue-next';
import { onMounted, ref, watch } from 'vue';
import { paymentPlanValueColumns } from './PaymentPlanValueColumns';

const showSection = ref<'graph' | 'table'>('table');
const isLoading = ref(true);
const graphData = ref<MappedPaymentPlanValueGraphData[]>([]);
const merchantStore = useMerchantStore();

onMounted(async () => await getPaymentPlanValueData());

const getPaymentPlanValueData = async () => {
  isLoading.value = true;

  try {
    const response = await getPaymentPlanGraphsData();
    const data = response.data.graph;

    if (Array.isArray(data)) {
      const filteredData = data.filter((item: PaymentPlanValueGraphData) => item._id !== 'OTHER');

      filteredData.sort((a: PaymentPlanValueGraphData, b: PaymentPlanValueGraphData) => {
        return dayjs(b.month).isBefore(dayjs(a.month)) ? -1 : 1;
      });

      graphData.value = filteredData.map((item: PaymentPlanValueGraphData) => {
        return {
          name: item.month,
          'Total Payment Plan Value': item.paymentPlanValue,
          'Average Payment Plan Value': item.averagePaymentPlanValue,
        };
      });
    }
  } catch (error) {
    if (error instanceof NetworkError) {
      createWarning(error);
      addMetric('payment-plan-value-graph', { status: 'network_error', channel: 'merchant' });
    } else {
      createException(error);
      addMetric('payment-plan-value-graph', { status: 'error', channel: 'merchant' });
    }
  } finally {
    isLoading.value = false;
  }
};

watch(
  () => merchantStore.merchant,
  async (newMerchant) => {
    if (newMerchant) {
      await getPaymentPlanValueData();
    }
  },
);
</script>
